import { FileButton, Loader, Menu, Tooltip, UnstyledButton } from "@mantine/core";
import { Box, Image as ImageIcon, LibraryBig, Sparkles, Upload, X } from "lucide-react";
import { useRef, type ReactElement } from "react";

import { IMAGE_TYPES } from "./ImagePicker";
import classes from "./slots.module.css";

/** Live's words for a slot the current setup does not take. */
export const UNSUPPORTED = "This option is not available with the current setup";

/** The tile's words for what it holds: live's reference images, or keyframes (ours, #1169). */
const WORDS = {
  references: { label: "Image refs", upload: "Choose an image reference to upload" },
  keyframes: { label: "Keyframes", upload: "Choose a keyframe to upload" },
} as const;

export interface ImageRefs {
  /**
   * Live's reference images, or a video's keyframes between its frames (Kling 4.0's, spec
   * web/keyframes-slot): the same tile, each keyframe's pill its place in the order.
   */
  holds?: keyof typeof WORDS | undefined;
  /** The chosen references, each with its picture and name. */
  images: { assetId: string; url: string | null; name: string }[];
  /** Live's `@ImageN` pill on each, where the model tags images. */
  tagged: boolean;
  /** The modes the inputs left take no references (live greys the tile). */
  disabled: boolean;
  /** No more fit: the tile greys out, with no message (live's Studio). */
  full: boolean;
  /** Live's Music: at its most the tile goes, and comes back once an image is removed. */
  hideWhenFull?: boolean | undefined;
  onFromLibrary: () => void;
  /** Live's "Generate", its Generate image dialog (`useGenerateImage`): the saved image is added. */
  onGenerate: () => void;
  /** Live's "From product"; none where the form offers no products (live's Music). */
  onFromProduct?: (() => void) | undefined;
  /** The files chosen: one, or several where `multiple` (live's Music takes several at once). */
  onUpload: (files: File[]) => void;
  multiple?: boolean | undefined;
  uploading: boolean;
  onRemove: (assetId: string) => void;
  /** Makes the tile the product popover's anchor. */
  wrap: (element: ReactElement) => ReactElement;
  /** Live's slot tooltip: the mode's image limits (none where it has none, #260). */
  limits: string[];
}

/**
 * Live's "Image refs" (#232, captures 03, 14, L04 to L04b): the references as pictures before the
 * tile, each with live's round x and, where the model tags images, its "@Image1" pill; the tile
 * opens live's menu (From library, Generate, Upload, From product), greyed with live's words
 * where the inputs chosen leave no mode that takes references. At its most the tile greys, or,
 * as live's Music does (#598), goes.
 */
export function ImageRefsSlot({ refs }: { refs: ImageRefs }) {
  const resetChooser = useRef<() => void>(null);
  const hidden = refs.full && refs.hideWhenFull === true && !refs.disabled;
  const keyframes = refs.holds === "keyframes";
  const words = WORDS[refs.holds ?? "references"];
  const tile = (
    <UnstyledButton
      className={classes.slot}
      aria-label={words.label}
      aria-busy={refs.uploading}
      disabled={refs.disabled || refs.full}
    >
      {refs.uploading ? <Loader size={20} aria-hidden /> : <ImageIcon size={20} strokeWidth={1.5} />}
      <span className={classes.slotLabel}>{words.label}</span>
    </UnstyledButton>
  );
  return (
    <>
      {refs.images.map((image, at) => (
        <span key={image.assetId} className={classes.slotFilled}>
          <span className={classes.slotChosen}>
            {image.url === null ? (
              <ImageIcon size={20} strokeWidth={1.5} aria-hidden />
            ) : (
              <img src={image.url} alt={image.name} className={classes.slotPicture} />
            )}
            {keyframes ? (
              <span className={classes.tag}>{at + 1}</span>
            ) : refs.tagged ? (
              <span className={classes.tag}>@Image{at + 1}</span>
            ) : null}
          </span>
          <UnstyledButton
            className={classes.thumbRemove}
            aria-label={`Remove ${image.name}`}
            onClick={() => refs.onRemove(image.assetId)}
          >
            <X size={14} strokeWidth={2.5} aria-hidden />
          </UnstyledButton>
        </span>
      ))}
      {hidden ? null : refs.disabled ? (
        <Tooltip label={UNSUPPORTED} withArrow>
          <span className={classes.slotFilled}>{tile}</span>
        </Tooltip>
      ) : (
        <Tooltip
          label={refs.limits.map((line) => (
            <div key={line}>{line}</div>
          ))}
          disabled={refs.limits.length === 0}
          multiline
          withArrow
        >
          {refs.wrap(
            <span className={classes.slotFilled}>
              <FileButton
                accept={IMAGE_TYPES.join(",")}
                multiple={refs.multiple === true}
                resetRef={resetChooser}
                onChange={(chosen: File | File[] | null) => {
                  const files = Array.isArray(chosen) ? chosen : chosen === null ? [] : [chosen];
                  if (files.length > 0) refs.onUpload(files);
                  resetChooser.current?.();
                }}
                inputProps={{ "aria-label": words.upload }}
              >
                {(chooser) => (
                  <Menu position="top-start" disabled={refs.full}>
                    <Menu.Target>{tile}</Menu.Target>
                    <Menu.Dropdown className={classes.frameMenu}>
                      <Menu.Item
                        leftSection={<LibraryBig size={16} />}
                        onClick={refs.onFromLibrary}
                      >
                        From library
                      </Menu.Item>
                      <Menu.Item leftSection={<Sparkles size={16} />} onClick={refs.onGenerate}>
                        Generate
                      </Menu.Item>
                      <Menu.Item leftSection={<Upload size={16} />} onClick={chooser.onClick}>
                        Upload
                      </Menu.Item>
                      {refs.onFromProduct === undefined ? null : (
                        <Menu.Item leftSection={<Box size={16} />} onClick={refs.onFromProduct}>
                          From product
                        </Menu.Item>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                )}
              </FileButton>
            </span>,
          )}
        </Tooltip>
      )}
    </>
  );
}
